import { Reminder, Task, UpdateTask } from '@griply/types';
import { getTimeFormat, resolveTaskReminders } from '@griply/utils';
import { useUpdateTasks } from 'features/tasks';
import React, { useState } from 'react';
import { RemindersPopupMenu } from 'shared/components/ui/reminders-popup-menu';
import { useUser } from 'shared/hooks/use-user';
import { isNumber } from 'shared/utils/is-number';

export type RemindersSubMenuProps = Omit<
  React.ComponentProps<typeof RemindersPopupMenu>,
  'reminders' | 'hasTime' | 'timeFormat' | 'onChange' | 'onClose'
> & {
  tasks: Task[];
  onClose: () => void;
  onUpdate?: (task: Task, updates: UpdateTask) => void;
};

// Habits carry no top-level timeslot — their start time lives on the schedule.
const getStartTime = (task: Task) =>
  task.timeslot?.startTime ?? task.schedules?.[0]?.timeslot?.startTime;

export const RemindersSubMenu: React.FC<RemindersSubMenuProps> = ({
  tasks,
  onClose,
  onUpdate,
  ...rest
}) => {
  const { submit: updateTasks } = useUpdateTasks();
  const user = useUser();
  const timeFormat = getTimeFormat(user);

  const [firstTask] = tasks;
  const hasTime =
    !!tasks.length && tasks.every((task) => isNumber(getStartTime(task)));

  // One task ⇒ seed its effective reminders. Several ⇒ the current value is
  // ambiguous, so start empty and only write what the user actually picks.
  const [reminders, setReminders] = useState<Reminder[]>(() =>
    tasks.length === 1
      ? resolveTaskReminders(firstTask.reminders, firstTask.reminderTime) ?? []
      : [],
  );

  const applyReminders = (next: Reminder[]) => {
    setReminders(next);

    if (onUpdate) {
      tasks.forEach((task) => onUpdate(task, { id: task.id, reminders: next }));
      return;
    }

    updateTasks(tasks.map((task) => ({ id: task.id, reminders: next })));
  };

  return (
    <RemindersPopupMenu
      {...rest}
      reminders={reminders}
      hasTime={hasTime}
      timeFormat={timeFormat}
      onChange={applyReminders}
      onClose={onClose}
    />
  );
};
